import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { customerAPI } from "../../utils/api";
import {
  FileText,
  Download,
  Eye,
  CheckCircle,
  Clock,
  AlertCircle,
  Search,
  Loader2,
  DollarSign,
  Calendar,
  ChevronLeft,
  ChevronRight,
  X,
  CreditCard,
  Banknote,
  Wallet,
} from "lucide-react";
import toast from "react-hot-toast";

const statusConfig = {
  draft: {
    color: "bg-gray-100 text-gray-800 border-gray-200",
    label: "Draft",
    icon: FileText,
  },
  sent: {
    color: "bg-blue-100 text-blue-800 border-blue-200",
    label: "Sent",
    icon: Clock,
  },
  paid: {
    color: "bg-emerald-100 text-emerald-800 border-emerald-200",
    label: "Paid",
    icon: CheckCircle,
  },
  partial: {
    color: "bg-amber-100 text-amber-800 border-amber-200",
    label: "Partial",
    icon: DollarSign,
  },
  overdue: {
    color: "bg-rose-100 text-rose-800 border-rose-200",
    label: "Overdue",
    icon: AlertCircle,
  },
  cancelled: {
    color: "bg-slate-100 text-slate-800 border-slate-200",
    label: "Cancelled",
    icon: X,
  },
};

const paymentIcon = {
  cash: Banknote,
  card: CreditCard,
  online: Wallet,
  bank_transfer: CreditCard,
  wallet: Wallet,
};

const formatMoney = (val) => {
  const num =
    typeof val === "string"
      ? parseFloat(val)
      : typeof val === "number"
        ? val
        : 0;
  return isNaN(num) ? "0.00" : num.toFixed(2);
};

const formatDate = (date) => {
  if (!date) return "N/A";
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const isOverdue = (invoice) => {
  return (
    invoice.status !== "paid" &&
    invoice.due_date &&
    new Date(invoice.due_date) < new Date()
  );
};

const CustomerInvoices = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const {
    data: invoicesData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["customerInvoices", page, search, statusFilter],
    queryFn: () =>
      customerAPI.invoices({
        page,
        per_page: 10,
        search: search || undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
      }),
    keepPreviousData: true,
  });

  const invoices = invoicesData?.data?.data?.data || [];
  const pagination = invoicesData?.data?.data || {};
  const totalPages = pagination.last_page || 1;

  const handleDownload = async (id) => {
    try {
      const response = await customerAPI.downloadInvoice(id);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `invoice-${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Invoice downloaded");
    } catch (err) {
      toast.error("Failed to download invoice");
    }
  };

  const filteredInvoices = invoices.filter((inv) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      inv.invoice_number?.toLowerCase().includes(s) ||
      inv.order?.tracking_number?.toLowerCase().includes(s)
    );
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-12 h-12 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500">
        <AlertCircle className="w-12 h-12 text-red-400 mb-3" />
        <p className="font-medium">Failed to load invoices</p>
        <p className="text-sm mt-1">{error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Invoices</h1>
          <p className="text-gray-600 mt-1">
            View and download your billing invoices
          </p>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card p-4 border-l-4 border-emerald-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500">Paid</p>
              <p className="text-lg font-bold text-gray-900">
                EGP{" "}
                {formatMoney(
                  invoices
                    .filter((i) => i.status === "paid")
                    .reduce(
                      (sum, i) => sum + parseFloat(i.total_amount || 0),
                      0,
                    ),
                )}
              </p>
            </div>
            <div className="p-2 bg-emerald-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
          </div>
        </div>
        <div className="card p-4 border-l-4 border-amber-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500">Pending</p>
              <p className="text-lg font-bold text-gray-900">
                EGP{" "}
                {formatMoney(
                  invoices
                    .filter(
                      (i) => i.status !== "paid" && i.status !== "cancelled",
                    )
                    .reduce(
                      (sum, i) =>
                        sum + parseFloat(i.balance_due || i.total_amount || 0),
                      0,
                    ),
                )}
              </p>
            </div>
            <div className="p-2 bg-amber-100 rounded-lg">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
          </div>
        </div>
        <div className="card p-4 border-l-4 border-rose-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500">Overdue</p>
              <p className="text-lg font-bold text-gray-900">
                EGP{" "}
                {formatMoney(
                  invoices
                    .filter((i) => isOverdue(i))
                    .reduce(
                      (sum, i) =>
                        sum + parseFloat(i.balance_due || i.total_amount || 0),
                      0,
                    ),
                )}
              </p>
            </div>
            <div className="p-2 bg-rose-100 rounded-lg">
              <AlertCircle className="w-5 h-5 text-rose-600" />
            </div>
          </div>
        </div>
        <div className="card p-4 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500">Total</p>
              <p className="text-lg font-bold text-gray-900">
                EGP{" "}
                {formatMoney(
                  invoices.reduce(
                    (sum, i) => sum + parseFloat(i.total_amount || 0),
                    0,
                  ),
                )}
              </p>
            </div>
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by invoice # or order #..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-field pl-10 w-full"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input-field md:w-48"
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="paid">Paid</option>
            <option value="partial">Partial</option>
            <option value="overdue">Overdue</option>
          </select>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                  Invoice #
                </th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                  Order
                </th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                  Date
                </th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                  Due Date
                </th>
                <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">
                  Amount
                </th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                  Status
                </th>
                <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-gray-400">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="font-medium">No invoices found</p>
                    <p className="text-sm mt-1">
                      Invoices will appear here once created
                    </p>
                  </td>
                </tr>
              ) : (
                filteredInvoices.map((invoice) => {
                  const StatusIcon =
                    statusConfig[invoice.status]?.icon || FileText;
                  const isInvoiceOverdue = isOverdue(invoice);
                  const PaymentIcon =
                    paymentIcon[invoice.payment_method] || CreditCard;

                  return (
                    <tr
                      key={invoice.id}
                      className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                        isInvoiceOverdue ? "bg-rose-50/50" : ""
                      }`}
                    >
                      <td className="py-3 px-4">
                        <span className="font-mono font-semibold text-gray-900">
                          {invoice.invoice_number}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {invoice.order ? (
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {invoice.order.tracking_number}
                            </p>
                            <p className="text-xs text-gray-500">
                              {invoice.order.status}
                            </p>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {formatDate(invoice.issue_date)}
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`text-sm ${
                            isInvoiceOverdue
                              ? "text-rose-600 font-medium"
                              : "text-gray-600"
                          }`}
                        >
                          {formatDate(invoice.due_date)}
                          {isInvoiceOverdue && (
                            <span className="ml-1 text-xs">(Overdue)</span>
                          )}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <p className="text-sm font-semibold text-gray-900">
                          EGP {formatMoney(invoice.total_amount)}
                        </p>
                        {invoice.balance_due > 0 &&
                          invoice.status !== "paid" && (
                            <p className="text-xs text-amber-600">
                              Due: EGP {formatMoney(invoice.balance_due)}
                            </p>
                          )}
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${
                            isInvoiceOverdue
                              ? "bg-rose-100 text-rose-800 border-rose-200"
                              : statusConfig[invoice.status]?.color
                          }`}
                        >
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {isInvoiceOverdue
                            ? "Overdue"
                            : statusConfig[invoice.status]?.label ||
                              invoice.status}
                        </span>
                        {invoice.payment_method &&
                          invoice.status === "paid" && (
                            <div className="flex items-center mt-1 text-xs text-gray-500">
                              <PaymentIcon className="w-3 h-3 mr-1" />
                              {invoice.payment_method}
                            </div>
                          )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end space-x-1">
                          <button
                            onClick={() => {
                              setSelectedInvoice(invoice);
                              setShowDetailModal(true);
                            }}
                            className="p-2 text-gray-400 hover:text-blue-600 transition-colors rounded-lg hover:bg-blue-50"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDownload(invoice.id)}
                            className="p-2 text-gray-400 hover:text-emerald-600 transition-colors rounded-lg hover:bg-emerald-50"
                            title="Download PDF"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <div className="text-sm text-gray-500">
              Page {page} of {totalPages}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Invoice {selectedInvoice.invoice_number}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {formatDate(selectedInvoice.issue_date)} - Due:{" "}
                  {formatDate(selectedInvoice.due_date)}
                </p>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-xl">
                  <p className="text-xs font-medium text-gray-500 uppercase">
                    Status
                  </p>
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium mt-2 ${
                      statusConfig[selectedInvoice.status]?.color
                    }`}
                  >
                    {statusConfig[selectedInvoice.status]?.label ||
                      selectedInvoice.status}
                  </span>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl">
                  <p className="text-xs font-medium text-gray-500 uppercase">
                    Payment
                  </p>
                  <p className="text-sm font-medium text-gray-900 mt-2 capitalize">
                    {selectedInvoice.payment_method || "Not paid yet"}
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Subtotal</span>
                  <span className="font-medium">
                    EGP {formatMoney(selectedInvoice.subtotal)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Tax</span>
                  <span className="font-medium">
                    EGP {formatMoney(selectedInvoice.tax_amount)}
                  </span>
                </div>
                {selectedInvoice.discount_amount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Discount</span>
                    <span className="font-medium text-rose-600">
                      -EGP {formatMoney(selectedInvoice.discount_amount)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-base font-bold pt-3 border-t border-gray-200">
                  <span>Total</span>
                  <span className="text-primary-600">
                    EGP {formatMoney(selectedInvoice.total_amount)}
                  </span>
                </div>
                {selectedInvoice.balance_due > 0 && (
                  <div className="flex justify-between text-sm text-amber-600">
                    <span>Balance Due</span>
                    <span className="font-medium">
                      EGP {formatMoney(selectedInvoice.balance_due)}
                    </span>
                  </div>
                )}
              </div>

              {selectedInvoice.notes && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                  <p className="text-xs text-amber-700 font-medium">Notes</p>
                  <p className="text-sm text-amber-900 mt-1">
                    {selectedInvoice.notes}
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => handleDownload(selectedInvoice.id)}
                  className="flex-1 btn-primary py-2.5 flex items-center justify-center"
                >
                  <Download className="w-4 h-4 mr-2" /> Download PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerInvoices;
